// ─────────────────────────────────────────────────────────────────
//  PatientAppointments.jsx  —  v3  "Clinical Clarity" enhanced
//  • Header with eyebrow, count pill + PRN chip, gradient book button
//  • Upcoming / Past / All filter tabs (client-side, no API change)
//  • Skeleton shimmer loading, refined rows with gradient date-tiles
//  • Booking modal: gradient step icons, upgraded slot grid,
//    hero-gradient confirmation summary
//  Props unchanged: { patient, decoded, token, activePrn, isDependent }
// ─────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo } from "react";
import {
  Flex, Typography, Button, Modal, Steps,
  Select, DatePicker, Spin, Alert,
} from "antd";
import {
  CalendarOutlined, PlusOutlined, ClockCircleOutlined,
  UserOutlined, MedicineBoxOutlined, InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "axios";
import toast from "react-hot-toast";
import {
  G700, G500, G100, G50, GRAY4, GRAY6, GRAY7,
  SLATE, MUTED, WHITE,
  GRAD_BRAND, GRAD_HERO,
  RADIUS_LG, RADIUS_MD, RADIUS_SM, RADIUS_PILL,
  FF_DISPLAY, FF_BODY, FF_MONO,
  SHADOW_SM, SHADOW_BLUE, TRANSITION_SM,
} from "./patientTokens";

const { Title, Text } = Typography;

// ── Status badge ───────────────────────────────────────────────
const STATUS_MAP = {
  pending:         { color: "#d97706", bg: "#fef3c7", label: "Pending"          },
  booked:          { color: "#2563eb", bg: "#eff6ff", label: "Booked"           },
  confirmed:       { color: "#16a34a", bg: "#dcfce7", label: "Confirmed"        },
  checked_in:      { color: "#7c3aed", bg: "#ede9fe", label: "Checked In"       },
  in_consultation: { color: "#4f46e5", bg: "#e0e7ff", label: "In Consultation"  },
  rescheduled:     { color: "#d97706", bg: "#fef3c7", label: "Rescheduled"      },
  cancelled:       { color: "#dc2626", bg: "#fee2e2", label: "Cancelled"        },
  closed:          { color: "#6b7280", bg: "#f3f4f6", label: "Closed"           },
};

function AppointmentStatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.pending;
  return (
    <span style={{
      display:      "inline-flex",
      alignItems:   "center",
      gap:          5,
      padding:      "3px 12px",
      borderRadius: RADIUS_PILL,
      fontSize:     11.5,
      fontWeight:   600,
      background:   s.bg,
      color:        s.color,
      border:       `1px solid ${s.color}22`,
      fontFamily:   FF_BODY,
      whiteSpace:   "nowrap",
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: "50%",
        background: s.color, display: "inline-block",
      }} />
      {s.label}
    </span>
  );
}

// ── Appointment row (uses v3 .pd-row / .pd-date-tile styles) ───
function AppointmentItem({ appointment }) {
  const isPast = dayjs(appointment.appointment_date).isBefore(dayjs(), "day");
  const time = appointment.appointment_time?.slice(0, 5);

  return (
    <div className="pd-row clickable" style={{ minHeight: 72, cursor: "default" }}>
      <div className="pd-date-tile" style={{ width: 50, height: 52, opacity: isPast ? 0.65 : 1 }}>
        <span className="pd-dt-day" style={{
          fontSize: 17, fontWeight: 700, color: G700, lineHeight: 1, fontFamily: FF_DISPLAY,
        }}>
          {dayjs(appointment.appointment_date).format("D")}
        </span>
        <span className="pd-dt-mon" style={{
          fontSize: 8, fontWeight: 700, color: MUTED, marginTop: 2,
          textTransform: "uppercase", letterSpacing: "0.6px", fontFamily: FF_BODY,
        }}>
          {dayjs(appointment.appointment_date).format("MMM")}
        </span>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <Text style={{
            fontSize: 14, fontWeight: 600, color: SLATE,
            fontFamily: FF_BODY, letterSpacing: "-0.2px",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {dayjs(appointment.appointment_date).format("ddd, MMM D YYYY")}
          </Text>
          {time && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: 10.5, fontWeight: 600, color: G700,
              background: G50, border: `1px solid ${G100}`,
              padding: "2px 8px", borderRadius: RADIUS_PILL,
              fontFamily: FF_MONO, flexShrink: 0,
            }}>
              <ClockCircleOutlined style={{ fontSize: 10 }} /> {time}
            </span>
          )}
        </div>
        <Text style={{
          color: MUTED, fontSize: 12, fontFamily: FF_BODY,
          display: "block", marginTop: 3,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          Dr. {appointment.doctor_name || "—"}
          {appointment.specialty_name && ` · ${appointment.specialty_name}`}
        </Text>
      </div>

      <div style={{ flexShrink: 0 }}>
        <AppointmentStatusBadge status={appointment.status} />
      </div>
    </div>
  );
}

// ── Skeleton row (loading state) ───────────────────────────────
const SkeletonRow = () => (
  <div style={{
    display: "flex", alignItems: "center", gap: 16,
    padding: "14px 20px", minHeight: 72, borderBottom: `1px solid ${GRAY7}`,
  }}>
    <div className="pd-skeleton" style={{ width: 50, height: 52, borderRadius: RADIUS_SM }} />
    <div style={{ flex: 1 }}>
      <div className="pd-skeleton" style={{ width: "45%", height: 13, marginBottom: 8 }} />
      <div className="pd-skeleton" style={{ width: "65%", height: 11 }} />
    </div>
    <div className="pd-skeleton" style={{ width: 84, height: 22, borderRadius: RADIUS_PILL }} />
  </div>
);

// ── Filter tabs ────────────────────────────────────────────────
function FilterTabs({ value, onChange, counts }) {
  const tabs = [
    { key: "upcoming", label: "Upcoming", count: counts.upcoming },
    { key: "past",     label: "Past",     count: counts.past },
    { key: "all",      label: "All",      count: counts.all },
  ];
  return (
    <div style={{
      display: "inline-flex", gap: 4,
      background: GRAY7, border: `1px solid ${GRAY6}`,
      borderRadius: RADIUS_MD, padding: 4,
    }}>
      {tabs.map(t => {
        const active = value === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              height: 34, padding: "0 14px",
              borderRadius: RADIUS_SM, border: "none",
              background: active ? WHITE : "transparent",
              color: active ? G700 : MUTED,
              fontWeight: 600, fontSize: 12.5, fontFamily: FF_BODY,
              boxShadow: active ? SHADOW_SM : "none",
              transition: TRANSITION_SM,
            }}
          >
            {t.label}
            <span style={{
              fontSize: 10, fontWeight: 700, fontFamily: FF_MONO,
              background: active ? G50 : GRAY6,
              color: active ? G700 : MUTED,
              borderRadius: RADIUS_PILL, padding: "1px 7px",
            }}>
              {t.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Section wrapper inside modal ───────────────────────────────
function ModalSection({ icon, title, children }) {
  return (
    <div style={{
      background:   WHITE,
      border:       `1px solid ${GRAY6}`,
      borderRadius: RADIUS_MD,
      overflow:     "hidden",
    }}>
      <div style={{
        padding:      "12px 18px",
        borderBottom: `1px solid ${GRAY7}`,
        display:      "flex",
        alignItems:   "center",
        gap:          10,
        background:   G50,
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: 9,
          background: GRAD_BRAND, boxShadow: SHADOW_BLUE,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: WHITE, fontSize: 14, flexShrink: 0,
        }}>
          {icon}
        </div>
        <Text strong style={{ color: SLATE, fontSize: 13, fontFamily: FF_DISPLAY, letterSpacing: "-0.2px" }}>
          {title}
        </Text>
      </div>
      <div style={{ padding: "16px 18px" }}>{children}</div>
    </div>
  );
}

// ── Booking modal ──────────────────────────────────────────────
function BookAppointmentModal({ visible, onClose, token }) {
  const [step,        setStep]        = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [doctors,     setDoctors]     = useState([]);
  const [slots,       setSlots]       = useState([]);
  const [slotInterval,setSlotInterval]= useState(30);

  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedDoctor,    setSelectedDoctor]    = useState(null);
  const [selectedDate,      setSelectedDate]      = useState(null);
  const [selectedTime,      setSelectedTime]      = useState(null);

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    axios
      .get("/api/patient/specialties", { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.data.success) setSpecialties(res.data.data); })
      .catch(() => toast.error("Failed to load specialties"))
      .finally(() => setLoading(false));
  }, [visible, token]);

  const loadDoctors = (specialtyId) => {
    setLoading(true);
    axios
      .get(`/api/patient/doctors-by-specialty/${specialtyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        if (res.data.success) setDoctors(res.data.data);
        else { setDoctors([]); }
      })
      .catch(() => { setDoctors([]); toast.error("Failed to load doctors"); })
      .finally(() => setLoading(false));
  };

  const loadSlots = (scheduleId, date) => {
    if (!scheduleId || isNaN(parseInt(scheduleId))) {
      toast.error("Invalid doctor schedule");
      return;
    }
    setLoading(true);
    axios
      .get(`/api/patient/available-slots?scheduleId=${scheduleId}&date=${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        if (res.data.success) {
          setSlots(res.data.data);
          setSlotInterval(res.data.intervalMinutes || 30);
        } else {
          toast.error("No slots available");
        }
      })
      .catch(() => toast.error("Failed to load slots"))
      .finally(() => setLoading(false));
  };

  const reset = () => {
    setStep(0);
    setSelectedSpecialty(null); setSelectedDoctor(null);
    setSelectedDate(null); setSelectedTime(null);
    setDoctors([]); setSlots([]);
  };

  const handleSpecialtySelect = (val) => {
    const sp = specialties.find(s => s.specialty_id === val);
    setSelectedSpecialty(sp);
    setSelectedDoctor(null); setSelectedDate(null);
    setSelectedTime(null); setDoctors([]); setSlots([]);
    if (val) loadDoctors(val);
    setStep(1);
  };

  const handleDoctorSelect = (val) => {
    const doc = doctors.find(d => d.doctor_id === val);
    setSelectedDoctor(doc);
    setSelectedDate(null); setSelectedTime(null); setSlots([]);
    if (doc?.schedule_id) setStep(2);
    else toast.error("Doctor schedule not available");
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date); setSelectedTime(null); setSlots([]);
    if (date && selectedDoctor)
      loadSlots(selectedDoctor.schedule_id, date.format("YYYY-MM-DD"));
    setStep(3);
  };

  const handleTimeSelect = (time) => { setSelectedTime(time); setStep(4); };

  const handleSubmit = async () => {
    if (!selectedSpecialty || !selectedDoctor || !selectedDate || !selectedTime) {
      toast.error("Please complete all steps"); return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post("/api/patient/book-appointment", {
        doctorScheduleId: selectedDoctor.schedule_id,
        specialtyId:      selectedSpecialty.specialty_id,
        appointmentDate:  selectedDate.format("YYYY-MM-DD"),
        appointmentTime:  selectedTime,
        doctorName:       selectedDoctor.doctor_name,
        clinicDay:        selectedDoctor.clinic_day,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) { toast.success(res.data.message); onClose(); }
      else toast.error(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to book");
    } finally { setSubmitting(false); }
  };

  const STEPS    = ["Specialty", "Doctor", "Date", "Time", "Confirm"];
  const canNext  = [!!selectedSpecialty, !!selectedDoctor, !!selectedDate, !!selectedTime];

  const formatInterval = (mins) => {
    if (mins >= 60 && mins % 60 === 0) {
      const h = mins / 60;
      return `${h} hour${h > 1 ? "s" : ""} slots`;
    }
    return `${mins} min slots`;
  };

  const stepContent = [

    /* 0 ── Specialty */
    <ModalSection key="s0" icon={<MedicineBoxOutlined />} title="Choose a Specialty">
      <Text style={{ color: MUTED, fontSize: 12.5, fontFamily: FF_BODY, display: "block", marginBottom: 14 }}>
        Select the medical department you would like to book an appointment with.
      </Text>
      <Select
        placeholder="Select a specialty…"
        style={{ width: "100%" }}
        size="large"
        onChange={handleSpecialtySelect}
        value={selectedSpecialty?.specialty_id}
        loading={loading}
        showSearch
        filterOption={(input, option) =>
          (option?.children?.toString() || "").toLowerCase().includes(input.toLowerCase())
        }
      >
        {specialties.map(s => (
          <Select.Option key={s.specialty_id} value={s.specialty_id}>
            {s.specialty_name}{s.schedule_description ? ` — ${s.schedule_description}` : ""}
          </Select.Option>
        ))}
      </Select>
    </ModalSection>,

    /* 1 ── Doctor */
    <ModalSection key="s1" icon={<UserOutlined />} title="Choose a Doctor">
      <Text style={{ color: MUTED, fontSize: 12.5, fontFamily: FF_BODY, display: "block", marginBottom: 14 }}>
        Select an available doctor
        {selectedSpecialty ? ` in ${selectedSpecialty.specialty_name}` : ""}.
      </Text>
      {loading ? (
        <Flex justify="center" align="center" style={{ padding: "32px 0", gap: 12 }}>
          <Spin />
          <Text style={{ color: MUTED, fontFamily: FF_BODY, fontSize: 13 }}>Loading doctors…</Text>
        </Flex>
      ) : doctors.length === 0 ? (
        <div style={{
          textAlign:    "center",
          padding:      "36px 24px",
          background:   GRAY7,
          borderRadius: RADIUS_MD,
          border:       `1px dashed ${GRAY6}`,
        }}>
          <div style={{
            width: 52, height: 52,
            borderRadius: RADIUS_MD,
            background: WHITE,
            border: `1px solid ${GRAY6}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 12px",
            fontSize: 22, color: GRAY4,
          }}>
            <UserOutlined />
          </div>
          <Text strong style={{ color: SLATE, fontSize: 14, display: "block", marginBottom: 6, fontFamily: FF_BODY }}>
            No Doctors Available
          </Text>
          <Text style={{ color: MUTED, fontSize: 12.5, fontFamily: FF_BODY, maxWidth: 280, display: "block", margin: "0 auto" }}>
            There are currently no doctors available for{" "}
            <strong>{selectedSpecialty?.specialty_name || "this specialty"}</strong>.
            Please try a different specialty or check back later.
          </Text>
        </div>
      ) : (
        <>
          <Select
            placeholder="Choose a doctor…"
            style={{ width: "100%" }}
            size="large"
            onChange={handleDoctorSelect}
            value={selectedDoctor?.doctor_id}
            disabled={!selectedSpecialty}
            showSearch
            filterOption={(input, option) =>
              (option?.children?.toString() || "").toLowerCase().includes(input.toLowerCase())
            }
          >
            {doctors.map(d => (
              <Select.Option key={d.doctor_id} value={d.doctor_id}>
                {d.doctor_name} — {d.clinic_day}
                {d.start_time ? ` (${d.start_time.slice(0, 5)} – ${d.end_time?.slice(0, 5)})` : ""}
              </Select.Option>
            ))}
          </Select>

          {selectedDoctor && (
            <div style={{
              marginTop:    14,
              padding:      "14px 16px",
              background:   G50,
              borderRadius: RADIUS_MD,
              border:       `1px solid ${G100}`,
              display:      "flex",
              alignItems:   "center",
              gap:          14,
            }}>
              <div style={{
                width: 44, height: 44,
                borderRadius: "50%",
                background: GRAD_BRAND,
                boxShadow: SHADOW_BLUE,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: WHITE, fontSize: 18, flexShrink: 0,
              }}>
                <UserOutlined />
              </div>
              <div>
                <Text strong style={{ color: SLATE, fontSize: 14, fontFamily: FF_BODY, display: "block" }}>
                  Dr. {selectedDoctor.doctor_name}
                </Text>
                <Text style={{ color: MUTED, fontSize: 12, fontFamily: FF_BODY }}>
                  {selectedDoctor.clinic_day}
                  {selectedDoctor.start_time && ` · ${selectedDoctor.start_time.slice(0, 5)} – ${selectedDoctor.end_time?.slice(0, 5)}`}
                </Text>
              </div>
            </div>
          )}
        </>
      )}
    </ModalSection>,

    /* 2 ── Date */
    <ModalSection key="s2" icon={<CalendarOutlined />} title="Choose a Date">
      <Text style={{ color: MUTED, fontSize: 12.5, fontFamily: FF_BODY, display: "block", marginBottom: 14 }}>
        Select a date for your appointment. Only future dates are available.
      </Text>
      <DatePicker
        style={{ width: "100%" }}
        size="large"
        placeholder="Select appointment date…"
        disabledDate={c => c && c < dayjs().startOf("day")}
        onChange={handleDateSelect}
        value={selectedDate}
        disabled={!selectedDoctor}
        format="dddd, MMMM D YYYY"
      />
      {selectedDate && (
        <div style={{
          marginTop:    14,
          padding:      "12px 16px",
          background:   G50,
          borderRadius: RADIUS_MD,
          border:       `1px solid ${G100}`,
          display:      "flex",
          alignItems:   "center",
          gap:          10,
        }}>
          <CalendarOutlined style={{ color: G700, fontSize: 16 }} />
          <div>
            <Text style={{
              color: MUTED, fontSize: 10, display: "block",
              fontFamily: FF_MONO, letterSpacing: "0.6px",
            }}>
              SELECTED DATE
            </Text>
            <Text strong style={{ color: SLATE, fontSize: 14, fontFamily: FF_BODY }}>
              {selectedDate.format("dddd, MMMM D YYYY")}
            </Text>
          </div>
        </div>
      )}
    </ModalSection>,

    /* 3 ── Time */
    <ModalSection key="s3" icon={<ClockCircleOutlined />} title="Choose a Time Slot">
      <Flex align="center" justify="space-between" style={{ marginBottom: 14 }} wrap="wrap" gap={8}>
        <Text style={{ color: MUTED, fontSize: 12.5, fontFamily: FF_BODY }}>
          Available slots for{" "}
          {selectedDate ? <strong>{selectedDate.format("MMM D, YYYY")}</strong> : "the selected date"}.
          {" "}Greyed-out slots are already booked.
        </Text>
        {slots.length > 0 && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "3px 10px", borderRadius: RADIUS_PILL,
            background: G100, color: G700,
            fontSize: 11, fontWeight: 700, fontFamily: FF_BODY,
            whiteSpace: "nowrap", flexShrink: 0,
          }}>
            <ClockCircleOutlined style={{ fontSize: 10 }} />
            {formatInterval(slotInterval)}
          </span>
        )}
      </Flex>

      {loading ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="pd-skeleton" style={{ width: 90, height: 44, borderRadius: RADIUS_SM }} />
          ))}
        </div>
      ) : slots.length === 0 ? (
        <div style={{
          textAlign:    "center",
          padding:      "32px 24px",
          background:   GRAY7,
          borderRadius: RADIUS_MD,
          border:       `1px dashed ${GRAY6}`,
        }}>
          <ClockCircleOutlined style={{ fontSize: 28, color: GRAY4, display: "block", marginBottom: 10 }} />
          <Text strong style={{ color: SLATE, fontSize: 14, display: "block", marginBottom: 6, fontFamily: FF_BODY }}>
            No Slots Available
          </Text>
          <Text style={{ color: MUTED, fontSize: 12.5, fontFamily: FF_BODY }}>
            No time slots are available for this date. Please try a different date.
          </Text>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {slots.map(slot => {
              const isSelected = selectedTime === slot.time;
              return (
                <button
                  key={slot.time}
                  disabled={!slot.available}
                  onClick={() => slot.available && handleTimeSelect(slot.time)}
                  style={{
                    padding:      "10px 18px",
                    borderRadius: RADIUS_SM,
                    minHeight:    44,
                    minWidth:     90,
                    border:       `1.5px solid ${isSelected ? "transparent" : slot.available ? GRAY6 : "#e5e7eb"}`,
                    background:   isSelected ? GRAD_BRAND : slot.available ? WHITE : GRAY7,
                    color:        isSelected ? WHITE : slot.available ? SLATE : GRAY4,
                    fontWeight:   600,
                    fontSize:     13.5,
                    fontFamily:   FF_MONO,
                    cursor:       slot.available ? "pointer" : "not-allowed",
                    transition:   TRANSITION_SM,
                    opacity:      slot.available ? 1 : 0.5,
                    boxShadow:    isSelected ? SHADOW_BLUE : "none",
                    transform:    isSelected ? "translateY(-1px)" : "none",
                  }}
                  onMouseEnter={e => {
                    if (slot.available && !isSelected) {
                      e.currentTarget.style.borderColor = G700;
                      e.currentTarget.style.background = G50;
                    }
                  }}
                  onMouseLeave={e => {
                    if (slot.available && !isSelected) {
                      e.currentTarget.style.borderColor = GRAY6;
                      e.currentTarget.style.background = WHITE;
                    }
                  }}
                >
                  {slot.time}
                </button>
              );
            })}
          </div>
          <Flex justify="space-between" align="center" style={{ marginTop: 14 }} wrap="wrap" gap={8}>
            <Text style={{ color: MUTED, fontSize: 11.5, fontFamily: FF_BODY }}>
              <strong style={{ color: SLATE }}>
                {slots.filter(s => s.available).length}
              </strong>{" "}
              of {slots.length} slots available
            </Text>
            <Text style={{ color: MUTED, fontSize: 11, fontFamily: FF_BODY }}>
              Each slot is{" "}
              <strong style={{ color: SLATE }}>
                {slotInterval >= 60
                  ? `${slotInterval / 60} hour${slotInterval > 60 ? "s" : ""}`
                  : `${slotInterval} minutes`}
              </strong>
            </Text>
          </Flex>
        </>
      )}
    </ModalSection>,

    /* 4 ── Confirm */
    <div key="s4" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        position:     "relative",
        background:   GRAD_HERO,
        borderRadius: RADIUS_MD,
        padding:      "18px 20px",
        overflow:     "hidden",
      }}>
        {/* soft glow accents to match the Overview hero */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: "radial-gradient(340px 160px at 90% -20%, rgba(34,211,238,0.2), transparent 60%)",
        }} />
        <Text style={{
          color:         "rgba(255,255,255,0.7)",
          fontSize:      10.5,
          fontWeight:    700,
          textTransform: "uppercase",
          letterSpacing: "1px",
          display:       "block",
          marginBottom:  14,
          fontFamily:    FF_BODY,
          position:      "relative",
        }}>
          Appointment Summary
        </Text>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, position: "relative" }}>
          {[
            { icon: <MedicineBoxOutlined />, label: "Specialty", value: selectedSpecialty?.specialty_name },
            { icon: <UserOutlined />,        label: "Doctor",    value: `Dr. ${selectedDoctor?.doctor_name}` },
            { icon: <CalendarOutlined />,    label: "Date",      value: selectedDate?.format("dddd, MMM D YYYY") },
            { icon: <ClockCircleOutlined />, label: "Time",      value: selectedTime },
          ].map(row => (
            <Flex key={row.label} align="center" gap={12}>
              <div style={{
                width: 34, height: 34, borderRadius: RADIUS_SM,
                background: "rgba(255,255,255,0.14)",
                border: "1px solid rgba(255,255,255,0.18)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: WHITE, flexShrink: 0,
              }}>
                {row.icon}
              </div>
              <div style={{ minWidth: 0 }}>
                <Text style={{
                  color: "rgba(255,255,255,0.6)", fontSize: 10.5,
                  display: "block", fontFamily: FF_BODY,
                }}>
                  {row.label}
                </Text>
                <Text strong style={{
                  color: WHITE, fontSize: 14, fontFamily: FF_BODY,
                  display: "block", whiteSpace: "nowrap",
                  overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {row.value || "—"}
                </Text>
              </div>
            </Flex>
          ))}
        </div>
      </div>
      <Alert
        message="Awaiting Staff Approval"
        description="Your appointment request will be reviewed and confirmed by our clinical team. You will see the updated status in your appointments list."
        type="info"
        showIcon
        style={{ borderRadius: RADIUS_MD, fontSize: 13 }}
      />
    </div>,
  ];

  return (
    <>
      <style>{`
        .bam-modal .ant-modal-body { padding: 0 !important; }
        .bam-modal .ant-modal-content { border-radius: 20px !important; overflow: hidden; }
        .bam-modal .ant-modal-header { padding: 20px 24px 16px !important; border-bottom: 1px solid #f0f4f8; }
        .bam-modal .ant-modal-footer { padding: 14px 24px 20px !important; border-top: 1px solid #f0f4f8; }
        .bam-modal .ant-steps-item-process .ant-steps-item-icon { background: #0E5BC2; border-color: #0E5BC2; }
        .bam-modal .ant-steps-item-finish .ant-steps-item-icon { border-color: #0E5BC2; }
        .bam-modal .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon { color: #0E5BC2; }
        .bam-modal .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-content > .ant-steps-item-title::after { background: #0E5BC2; }
      `}</style>

      <Modal
        open={visible}
        onCancel={() => { reset(); onClose(); }}
        className="bam-modal"
        width="min(720px, 96vw)"
        style={{ top: 16 }}
        styles={{
          body: { padding: 0, maxHeight: "calc(100vh - 200px)", overflowY: "auto" },
        }}
        title={
          <Flex align="center" gap={12}>
            <div style={{
              width: 40, height: 40, borderRadius: RADIUS_MD,
              background: GRAD_BRAND, boxShadow: SHADOW_BLUE,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: WHITE, fontSize: 18,
            }}>
              <CalendarOutlined />
            </div>
            <div>
              <Text strong style={{ fontSize: 16, fontFamily: FF_DISPLAY, color: SLATE, display: "block", lineHeight: 1.2, letterSpacing: "-0.3px" }}>
                Book New Appointment
              </Text>
              <Text style={{ fontSize: 12, color: MUTED, fontFamily: FF_BODY }}>
                Complete all steps to submit your request
              </Text>
            </div>
          </Flex>
        }
        footer={
          <Flex justify="space-between" align="center">
            <Text style={{ color: MUTED, fontSize: 12, fontFamily: FF_MONO }}>
              Step {step + 1} / {STEPS.length}
            </Text>
            <Flex gap={10}>
              <Button
                size="large"
                onClick={() => { if (step > 0) setStep(step - 1); else { reset(); onClose(); } }}
                style={{ minHeight: 44, minWidth: 90, borderRadius: RADIUS_SM, fontWeight: 600 }}
              >
                {step === 0 ? "Cancel" : "← Back"}
              </Button>
              {step === STEPS.length - 1 ? (
                <Button
                  type="primary"
                  size="large"
                  loading={submitting}
                  onClick={handleSubmit}
                  style={{
                    minHeight: 44, minWidth: 160,
                    borderRadius: RADIUS_SM,
                    background: GRAD_BRAND,
                    boxShadow: SHADOW_BLUE,
                    border: "none", fontWeight: 600, fontFamily: FF_BODY,
                  }}
                >
                  Request Appointment
                </Button>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  disabled={!canNext[step]}
                  onClick={() => setStep(step + 1)}
                  style={{
                    minHeight: 44, minWidth: 120,
                    borderRadius: RADIUS_SM,
                    background: canNext[step] ? GRAD_BRAND : undefined,
                    boxShadow: canNext[step] ? SHADOW_BLUE : "none",
                    border: "none", fontWeight: 600, fontFamily: FF_BODY,
                  }}
                >
                  Next →
                </Button>
              )}
            </Flex>
          </Flex>
        }
      >
        {/* Progress steps */}
        <div style={{
          padding:      "16px 24px",
          borderBottom: `1px solid ${GRAY7}`,
          background:   GRAY7,
        }}>
          <Steps current={step} size="small" style={{ maxWidth: 600 }}>
            {STEPS.map((t, i) => (
              <Steps.Step
                key={i}
                title={<span style={{ fontSize: 12 }}>{t}</span>}
                status={i < step ? "finish" : i === step ? "process" : "wait"}
              />
            ))}
          </Steps>
        </div>

        {/* Step content */}
        <div style={{ padding: "24px 24px 20px", minHeight: 280 }}>
          {stepContent[step]}
        </div>
      </Modal>
    </>
  );
}

// ── MAIN ──────────────────────────────────────────────────────
function PatientAppointments({ patient, decoded, token, activePrn, isDependent }) {
  const [appointments,   setAppointments]   = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [bookingVisible, setBookingVisible] = useState(false);
  const [filter,         setFilter]         = useState("upcoming");

  const prn = patient?.prn || decoded?.prn || "—";

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      // Use dependent endpoint when viewing a dependent profile
      const url = isDependent && activePrn
        ? `/api/patient/dependent/${activePrn}/appointments`
        : "/api/patient/appointments";

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setAppointments(res.data.data);
    } catch {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, [token, activePrn, isDependent]); // eslint-disable-line

  // Client-side split — no API changes
  const { upcoming, past } = useMemo(() => {
    const today = dayjs().startOf("day");
    const up = [], pa = [];
    appointments.forEach(a => {
      (dayjs(a.appointment_date).isBefore(today) ? pa : up).push(a);
    });
    up.sort((a, b) => dayjs(a.appointment_date).diff(dayjs(b.appointment_date)));
    pa.sort((a, b) => dayjs(b.appointment_date).diff(dayjs(a.appointment_date)));
    return { upcoming: up, past: pa };
  }, [appointments]);

  const shown = filter === "upcoming" ? upcoming : filter === "past" ? past : appointments;
  const counts = { upcoming: upcoming.length, past: past.length, all: appointments.length };

  return (
    <div style={{ width: "100%" }}>

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="pd-up" style={{
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        gap: 16, flexWrap: "wrap", marginBottom: 20,
      }}>
        <div style={{ minWidth: 0 }}>
          <div className="pd-eyebrow"><CalendarOutlined /> Appointments</div>
          <Title level={3} style={{
            margin:        "2px 0 6px",
            fontFamily:    FF_DISPLAY,
            fontWeight:    800,
            color:         SLATE,
            letterSpacing: "-0.6px",
            fontSize:      "clamp(1.3rem, 3vw, 1.75rem)",
          }}>
            {isDependent
              ? `${patient?.full_name?.split(" ")[0] || "Dependent"}'s Appointments`
              : "Your Appointments"}
          </Title>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Text style={{ color: MUTED, fontSize: 13, fontFamily: FF_BODY }}>
              {appointments.length} appointment{appointments.length !== 1 ? "s" : ""} on record
            </Text>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "2px 10px", borderRadius: RADIUS_PILL,
              background: G50, border: `1px solid ${G100}`,
              color: G700, fontSize: 11, fontWeight: 600, fontFamily: FF_MONO,
            }}>
              PRN {prn}
            </span>
          </div>
        </div>

        {/* Book button — hidden when viewing a dependent */}
        {!isDependent && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => setBookingVisible(true)}
            style={{
              background:   GRAD_BRAND,
              border:       "none",
              borderRadius: RADIUS_MD,
              fontWeight:   600,
              fontFamily:   FF_BODY,
              boxShadow:    SHADOW_BLUE,
              height:       46,
              paddingLeft:  20,
              paddingRight: 20,
              flexShrink:   0,
            }}
          >
            Book New Appointment
          </Button>
        )}
      </div>

      {/* ── Dependent notice ───────────────────────────────── */}
      {isDependent && (
        <div className="pd-up pd-up-1" style={{
          background:   "#fffbeb",
          border:       "1px solid #fcd34d",
          borderLeft:   "3px solid #d97706",
          borderRadius: RADIUS_SM,
          padding:      "12px 16px",
          marginBottom: 20,
          display:      "flex",
          alignItems:   "center",
          gap:          12,
        }}>
          <InfoCircleOutlined style={{ color: "#d97706", fontSize: 16, flexShrink: 0 }} />
          <Text style={{ fontSize: 12.5, color: "#92400e", fontFamily: FF_BODY, fontWeight: 600 }}>
            To book an appointment for this patient, please visit the front desk
            or switch back to your own profile and contact the clinical team.
          </Text>
        </div>
      )}

      {/* ── Filter tabs ────────────────────────────────────── */}
      {!loading && appointments.length > 0 && (
        <div className="pd-up pd-up-1" style={{ marginBottom: 16 }}>
          <FilterTabs value={filter} onChange={setFilter} counts={counts} />
        </div>
      )}

      {/* ── Appointments list ──────────────────────────────── */}
      <div className="pd-panel pd-up pd-up-2">
        {loading ? (
          <>
            <SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow />
          </>
        ) : appointments.length === 0 ? (
          <div style={{ textAlign: "center", padding: "64px 24px" }}>
            <div style={{
              width: 64, height: 64,
              borderRadius: RADIUS_LG,
              background: GRAY7,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px",
              fontSize: 28, color: GRAY4,
            }}>
              <CalendarOutlined />
            </div>
            <Text strong style={{
              color: SLATE, fontSize: 15, display: "block",
              marginBottom: 6, fontFamily: FF_DISPLAY,
            }}>
              No Appointments Yet
            </Text>
            <Text style={{
              color: MUTED, fontSize: 13, fontFamily: FF_BODY,
              display: "block", marginBottom: 20,
            }}>
              {isDependent
                ? "No appointments found for this patient."
                : "You have no appointments on record. Book your first one below."}
            </Text>
            {!isDependent && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setBookingVisible(true)}
                style={{
                  background:   GRAD_BRAND,
                  border:       "none",
                  borderRadius: RADIUS_MD,
                  boxShadow:    SHADOW_BLUE,
                  fontWeight:   600,
                  height:       42,
                }}
              >
                Book an Appointment
              </Button>
            )}
          </div>
        ) : shown.length === 0 ? (
          <div style={{ textAlign: "center", padding: "42px 24px" }}>
            <Text strong style={{ color: SLATE, fontSize: 14, display: "block", marginBottom: 4, fontFamily: FF_DISPLAY }}>
              No {filter} appointments
            </Text>
            <Text style={{ color: MUTED, fontSize: 12.5, fontFamily: FF_BODY }}>
              Switch tabs to see your other appointments.
            </Text>
          </div>
        ) : (
          shown.map((app, idx) => (
            <div key={app.id} style={{ animation: `pdUp 0.28s ease ${Math.min(idx * 0.04, 0.3)}s both` }}>
              <AppointmentItem appointment={app} />
            </div>
          ))
        )}
      </div>

      {/* Booking modal — only mounted when not viewing a dependent */}
      {!isDependent && (
        <BookAppointmentModal
          visible={bookingVisible}
          onClose={() => { setBookingVisible(false); fetchAppointments(); }}
          token={token}
        />
      )}
    </div>
  );
}

export default PatientAppointments;